import { ThemeProvider } from '@emotion/react';
import { CssBaseline, createTheme } from '@mui/material';
import { onAuthStateChanged } from 'firebase/auth';
import { useContext, useEffect } from 'react';
import NavigationDrawer from './components/menu/NavigationDrawer';
import { ThemeContext } from './contexts/ThemeContextProvider';
import { auth } from './firebase/Firebase';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { setUser } from './store/slices/authSlice';
import { getAbsences } from './store/thunks/absencesThunks';
import { loadProfile } from './store/thunks/authThunks';
import { evaluateTimes, getTimes } from './store/thunks/timesThunks';
import A2HSInstaller from './utils/A2HSInstaller';
import { darkThemeOptions, lightThemeOptions } from './utils/MyThemeOptions';

const App = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  const themeContext = useContext(ThemeContext);
  const lightTheme = createTheme(lightThemeOptions);
  const darkTheme = createTheme(darkThemeOptions);
  const appliedTheme = createTheme(themeContext.light ? lightTheme : darkTheme);

  useEffect(() => {
    if (user && user.uid) {
      // Check if user is logged in
      dispatch(loadProfile(undefined)).then(() => {
        // Load times from firestore
        dispatch(getTimes(undefined));

        // Load absences from firestore
        dispatch(getAbsences(undefined));
      });
    }
  }, [user]);

  // Load user on startup
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) dispatch(setUser({ ...currentUser }));
    });
    return unsubscribe;
  }, []);

  const { times, timesLastRefresh } = useAppSelector((state) => state.times);
  const absencesLastRefresh = useAppSelector((state) => state.absences.absencesLastRefresh);

  useEffect(() => {
    if (times.length == 0) return;
    if (timesLastRefresh == 0) return;
    if (absencesLastRefresh == 0) return;

    dispatch(evaluateTimes(undefined));
  }, [timesLastRefresh, absencesLastRefresh]);

  return (
    <>
      <ThemeProvider theme={appliedTheme}>
        <CssBaseline />
        <NavigationDrawer />
        <A2HSInstaller />
      </ThemeProvider>
    </>
  );
};

export default App;
